<template>
    <div class="autocomplete-wrapper">

        <div v-if="localValue && !editing" class="autocomplete-selected form-control d-flex align-items-center justify-content-between">
            <button type="button" class="btn btn-sm p-0 me-2" @click="clear">
                <icon-close></icon-close>
            </button>
            <div class="flex-grow-1" @click="startEditing">
                {{ labelFn ? labelFn(localValue) : localValue }}
            </div>
        </div>

        <div v-else class="position-relative">
            <input ref="searchInput" type="text" class="form-control" v-model="searchText"
                @input="onInput"
                @keydown.down.prevent="moveDown"
                @keydown.up.prevent="moveUp"
                @keydown.enter.prevent="selectHighlighted"
                @keydown.esc="closeDropdown"
                @blur="onBlur"
            />
            <div class="autocomplete-dropdown" v-if="showDropdown">
                <div v-if="items.length === 0" class="autocomplete-item text-muted">
                    Nessun risultato
                </div>
                <div
                    v-for="(item, i) in items"
                    :key="i"
                    class="autocomplete-item"
                    :class="{ highlighted: i === highlightedIndex }"
                    @mousedown.prevent="selectItem(item)"
                    @mouseover="highlightedIndex = i"
                >
                    <slot name="item" :item="item" :highlight="highlight"></slot>
                </div>
            </div>
        </div>

    </div>
</template>

<script lang="ts">
import WithModel from '@/mixins/withModel';
import { mixins, Options } from 'vue-class-component';
import { Prop } from 'vue-property-decorator';

@Options({})
export default class Autocomplete extends mixins(WithModel) {

    declare localValue: any;

    @Prop() fetchFn: (searchText: string) => Promise<any[]>;
    @Prop() labelFn: (item: any) => string;

    searchText: string = '';
    items: any[] = [];
    showDropdown: boolean = false;
    highlightedIndex: number = -1;
    editing: boolean = false;
    debouncer = null;

    onInput() {
        this.highlightedIndex = -1;
        clearTimeout(this.debouncer);
        this.debouncer = setTimeout(() => {
            this.fetchFn(this.searchText)
            .then(x => {
                this.items = x;
                this.showDropdown = true;
            });
        }, 300);
    }

    moveDown() {
        if (this.highlightedIndex < this.items.length - 1)
            this.highlightedIndex++;
    }

    moveUp() {
        if (this.highlightedIndex > 0)
            this.highlightedIndex--;
    }

    selectHighlighted() {
        if (this.highlightedIndex >= 0 && this.highlightedIndex < this.items.length)
            this.selectItem(this.items[this.highlightedIndex]);
    }

    selectItem(item: any) {
        this.localValue = item;
        this.emitModel(this.localValue);
        this.searchText = '';
        this.items = [];
        this.showDropdown = false;
        this.highlightedIndex = -1;
    }

    clear() {
        this.localValue = null;
        this.emitModel(this.localValue);
        this.searchText = '';
        this.items = [];
        this.showDropdown = false;
        this.highlightedIndex = -1;
    }

    startEditing() {
        this.editing = true;
        if (this.labelFn && this.localValue)
            this.searchText = this.labelFn(this.localValue);
        this.$nextTick(() => {
            (this.$refs.searchInput as HTMLInputElement)?.focus();
        });
    }

    onBlur() {
        this.showDropdown = false;
        this.highlightedIndex = -1;
        if (this.localValue)
            this.editing = false;
    }

    highlight(text: string): string {
        if (!this.searchText || !text) return text;
        const escaped = this.searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${escaped})`, 'gi');
        return text.replace(regex, '<u>$1</u>');
    }

    closeDropdown() {
        this.showDropdown = false;
        this.highlightedIndex = -1;
    }
}
</script>

<style scoped>
.autocomplete-wrapper {
    position: relative;
}
.autocomplete-selected {
    min-height: 38px;
}
.autocomplete-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
    background: white;
    border: 1px solid #dee2e6;
    border-top: none;
    border-radius: 0 0 4px 4px;
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    max-height: 240px;
    overflow-y: auto;
}
.autocomplete-item {
    padding: 8px 12px;
    cursor: pointer;
}
.autocomplete-item.highlighted {
    background-color: #f0f0f0;
}
</style>
